import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Edit, 
  Trash2, 
  MoreVertical,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { localSchedulesService } from '@/services';
import type { ApiLocalSchedule, SyncStatus } from '@/types/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { LocalScheduleDeleteDialog } from './LocalScheduleDeleteDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  VerificationBadge,
  isVerificationIssue,
} from '@/components/schedules/VerificationBadge';

interface LocalScheduleCardProps {
  schedule: ApiLocalSchedule;
  onEdit: (schedule: ApiLocalSchedule) => void;
  controllerId: string;
}

const getSyncStatusIcon = (status: SyncStatus) => {
  switch (status) {
    case 'synced':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'syncing':
      return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
    case 'error':
      return <XCircle className="w-4 h-4 text-red-600" />;
    case 'pending':
      return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    default:
      return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
};

const getSyncStatusLabel = (status: SyncStatus) => {
  switch (status) {
    case 'synced':
      return 'Sincronizada';
    case 'syncing':
      return 'Sincronizando...';
    case 'error':
      return 'Error';
    case 'pending':
      return 'Pendiente';
    default:
      return 'Desconocido';
  }
};

const getSyncStatusColor = (status: SyncStatus) => {
  switch (status) {
    case 'synced':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'syncing':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'error':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export function LocalScheduleCard({ schedule, onEdit, controllerId }: LocalScheduleCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Mutation para reintentar sincronización
  const retrySyncMutation = useMutation({
    mutationFn: async () => {
      const byIdResponse = await localSchedulesService.syncById(schedule.id);
      if (byIdResponse.success) {
        return byIdResponse.data;
      }
      if (byIdResponse.error?.code === 'HTTP_404') {
        const slotResponse = await localSchedulesService.sync(controllerId, {
          slots: [schedule.slotNumber],
        });
        if (!slotResponse.success) {
          throw new Error(slotResponse.error?.message || 'Error al sincronizar programación');
        }
        return slotResponse.data;
      }
      throw new Error(byIdResponse.error?.message || 'Error al sincronizar programación');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] });
      toast({
        title: 'Sincronización iniciada',
        description:
          'Se sincronizan zona horaria, hora y programación. En LoRaWAN puede tardar varios minutos.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const canRetrySync =
    schedule.syncStatus === 'error' ||
    schedule.syncStatus === 'pending' ||
    schedule.syncStatus === 'syncing' ||
    isVerificationIssue(schedule.verificationStatus);
  const isSyncing = schedule.syncStatus === 'syncing' || retrySyncMutation.isPending;

  // Mutation para activar/desactivar (endpoint dedicado POST /local-schedules/{id}/toggle)
  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await localSchedulesService.toggle(schedule.id, enabled);
      if (!response.success) {
        const msg = response.error?.message ?? (response as { message?: string }).message;
        throw new Error(msg || 'Error al activar/desactivar programación');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] });
      toast({
        title: data?.enabled ? 'Programación activada' : 'Programación desactivada',
        description: data?.enabled
          ? 'La programación se sincronizará con el dispositivo.'
          : 'La programación local está pausada.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return (
    <>
      <Card className={cn(
        'group hover:shadow-card-hover transition-all duration-300 cursor-pointer w-full',
        !schedule.enabled && 'opacity-60'
      )}
      onClick={() => onEdit(schedule)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h4 className="font-display font-semibold truncate">
                  {schedule.name || `Plan ${schedule.slotNumber}`}
                </h4>
                <Badge variant="outline" className="text-xs flex-shrink-0">
                  Slot {schedule.slotNumber}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <div
                  className="flex items-center gap-2 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  title={schedule.enabled ? 'Desactivar programación' : 'Activar programación'}
                >
                  <Switch
                    checked={schedule.enabled}
                    disabled={toggleMutation.isPending}
                    onCheckedChange={(checked) => {
                      if (!toggleMutation.isPending) toggleMutation.mutate(checked);
                    }}
                  />
                  <span className="text-xs font-medium text-muted-foreground">
                    {schedule.enabled ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <Badge variant="outline" className={cn('text-xs flex-shrink-0', getSyncStatusColor(schedule.syncStatus))}>
                  <span className="flex items-center gap-1">
                    {getSyncStatusIcon(schedule.syncStatus)}
                    <span className="truncate">{getSyncStatusLabel(schedule.syncStatus)}</span>
                  </span>
                </Badge>
                <VerificationBadge status={schedule.verificationStatus} />
                {schedule.source === 'device' && (
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    Desde dispositivo
                  </Badge>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation(); // Evitar que el click se propague a la card
                  }}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  onEdit(schedule);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!toggleMutation.isPending) toggleMutation.mutate(!schedule.enabled);
                  }}
                  disabled={toggleMutation.isPending || isSyncing}
                >
                  {schedule.enabled ? 'Desactivar' : 'Activar'}
                </DropdownMenuItem>
                {canRetrySync && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!retrySyncMutation.isPending) retrySyncMutation.mutate();
                    }}
                    disabled={retrySyncMutation.isPending}
                  >
                    <RefreshCw className={cn('w-4 h-4 mr-2', retrySyncMutation.isPending && 'animate-spin')} />
                    {isVerificationIssue(schedule.verificationStatus)
                      ? 'Enviar al dispositivo'
                      : 'Reintentar envío'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={isSyncing}
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Información principal en grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Horario */}
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-medium">{schedule.startTime}</span>
                <span className="text-muted-foreground">-</span>
                <span className="font-medium">{schedule.endTime}</span>
                <span className="text-muted-foreground">({schedule.durationMinutes} min)</span>
              </div>
            </div>

            {/* Válvulas */}
            <div className="text-sm">
              <span className="text-muted-foreground">Válvulas: </span>
              <span className="font-medium">{schedule.valveName}</span>
            </div>
          </div>

          {/* Días de la semana */}
          <div className="flex flex-wrap gap-1">
            {schedule.daysOfWeek.map((day, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {day}
              </Badge>
            ))}
          </div>

          {/* Detalles adicionales */}
          {(schedule.waterPulses > 0 || schedule.flowControlEnabled) && (
            <div className="pt-2 border-t space-y-1 text-xs text-muted-foreground">
              {schedule.waterPulses > 0 && (
                <div>Límite de pulsos: {schedule.waterPulses}</div>
              )}
              {schedule.flowControlEnabled && (
                <div>Control de flujo: Activado</div>
              )}
            </div>
          )}

          {/* Última sincronización */}
          {schedule.syncedAt && (
            <div className="pt-2 border-t text-xs text-muted-foreground">
              Última sincronización: {new Date(schedule.syncedAt).toLocaleString('es-ES')}
            </div>
          )}

          {/* Error de sincronización */}
          {schedule.syncError && (
            <Alert variant="destructive" className="mt-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {schedule.syncError}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de eliminación */}
      <LocalScheduleDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        schedule={schedule}
        controllerId={controllerId}
      />
    </>
  );
}
